import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { SearchSelect } from "src/components/Inputs/SearchSelect";
import { RuleType } from "src/types/formEngineRule";

export type Props = {
  hasSchoolSelectionSection: boolean;
  isSchoolRankingEnabled: boolean;
  lotteryOffersEnabled: boolean;
  isAddRuleModalOpen: boolean;
  setIsAddRuleModalOpen: (state: boolean) => void;
  selectedRuleType: RuleType | "";
  setSelectedRuleType: (ruleType: RuleType | "") => void;
  hasExistingLegacyRules: boolean;
  hasExistingClearLowerRanked: boolean;
  handleRuleTypeSelection: () => void;
};

const SCHOOL_RANKING_TOOLTIP =
  "Only available on forms with a school ranking section";
const ORDERED_RANKING_TOOLTIP =
  "Only available on forms with ordered school ranking";
const LOTTERY_OFFERS_TOOLTIP =
  "Only available when lottery and offers are enabled";
const CONNECT_FORMS_EXISTS_TOOLTIP = "A connect forms rule already exists";
const CLEAR_LOWER_RANKED_EXISTS_TOOLTIP =
  "A clear lower ranked waitlists and offers rule already exists";

export const AddRuleModal: React.FC<Props> = ({
  hasSchoolSelectionSection,
  isSchoolRankingEnabled,
  lotteryOffersEnabled,
  isAddRuleModalOpen,
  setIsAddRuleModalOpen,
  setSelectedRuleType,
  hasExistingLegacyRules,
  hasExistingClearLowerRanked,
  selectedRuleType,
  handleRuleTypeSelection,
}: Props) => {
  const waitlistDisabled = !hasSchoolSelectionSection || !lotteryOffersEnabled;
  const waitlistTooltip = !hasSchoolSelectionSection
    ? SCHOOL_RANKING_TOOLTIP
    : LOTTERY_OFFERS_TOOLTIP;

  const clearLowerRankedDisabled =
    !hasSchoolSelectionSection ||
    !isSchoolRankingEnabled ||
    hasExistingClearLowerRanked;
  const clearLowerRankedTooltip = !hasSchoolSelectionSection
    ? SCHOOL_RANKING_TOOLTIP
    : !isSchoolRankingEnabled
    ? ORDERED_RANKING_TOOLTIP
    : CLEAR_LOWER_RANKED_EXISTS_TOOLTIP;

  const items = [
    {
      key: "connect_forms",
      label: `Connect forms`,
      description: `Given a certain condition, allow starting a new form`,
      disabled: hasExistingLegacyRules,
      disabledTooltip: hasExistingLegacyRules
        ? CONNECT_FORMS_EXISTS_TOOLTIP
        : undefined,
    },
    {
      key: "auto_revoke",
      label: `Revoke offers`,
      description: `Given a certain condition, automatically revoke offers`,
      disabled: !hasSchoolSelectionSection,
      disabledTooltip: !hasSchoolSelectionSection
        ? SCHOOL_RANKING_TOOLTIP
        : undefined,
    },
    {
      key: "clear_lower_ranked",
      label: `Clear lower ranked waitlists and offers`,
      description: `Upon offer acceptance, automatically clear waitlists and offers at lower ranked schools.`,
      disabled: clearLowerRankedDisabled,
      disabledTooltip: clearLowerRankedDisabled
        ? clearLowerRankedTooltip
        : undefined,
    },
    {
      key: "auto_tag",
      label: `Tag`,
      description: `Apply or remove tags automatically based on something`,
      disabled: !hasSchoolSelectionSection,
      disabledTooltip: !hasSchoolSelectionSection
        ? SCHOOL_RANKING_TOOLTIP
        : undefined,
    },
    {
      key: "auto_waitlist",
      label: `Waitlist and optionally make offers`,
      description: `Based on criteria, automatically waitlist responses. Optionally, make offers.`,
      disabled: waitlistDisabled,
      disabledTooltip: waitlistDisabled ? waitlistTooltip : undefined,
    },
  ];

  const selectedItem =
    items.find((item) => item.key === selectedRuleType) ?? null;

  return (
    <Modal
      isOpen={isAddRuleModalOpen}
      onClose={() => {
        setIsAddRuleModalOpen(false);
        setSelectedRuleType("");
      }}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create rule</ModalHeader>
        <ModalBody>
          <SearchSelect
            label="What would you like to do?"
            placeholder="Select an option"
            items={items}
            selectedItem={selectedItem}
            onChange={(item) =>
              setSelectedRuleType((item?.key as RuleType) || "")
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={() => {
              setIsAddRuleModalOpen(false);
              setSelectedRuleType("");
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleRuleTypeSelection}
            isDisabled={!selectedRuleType}
          >
            Next
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
